/** Variant picker (draft orders, order edits): search products, expand to variants, pick with a quantity. */
import React, { useEffect, useState } from 'react';
import { Button, Empty, Input, InputNumber, List, Modal, Space, Spin, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { listOf, money, toastErr, useSite } from './common';
import { Thumb } from './ShopCommon';

const { Text } = Typography;

/** onPick({variant_id, product_id, title, variant_title, price, quantity, image}) — called once per added line */
const ShopVariantPicker = ({ open, onClose, onPick }) => {
    const { t } = useTranslation();
    const { api, currency } = useSite();
    const [q, setQ] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openId, setOpenId] = useState(null);
    const [variants, setVariants] = useState({});
    const [qty, setQty] = useState({});

    useEffect(() => {
        if (!open) return undefined;
        const h = setTimeout(async () => {
            setLoading(true);
            try { setRows(listOf(await api.get('/products', { q, per_page: 30, status: 'active' }))); } catch (e) { toastErr(e); } finally { setLoading(false); }
        }, 250);
        return () => clearTimeout(h);
    }, [open, q, api]);

    const expand = async (p) => {
        setOpenId(openId === p.id ? null : p.id);
        if (variants[p.id]) return;
        try {
            const d = await api.get(`/products/${p.id}`);
            const prod = d?.product || d;
            setVariants((v) => ({ ...v, [p.id]: (prod.variants || []).map((x) => ({ ...x, product_title: prod.title, image: x.image || prod.images?.[0] || p.image })) }));
        } catch (e) { toastErr(e); }
    };
    const add = (v) => {
        onPick({ variant_id: v.id, product_id: v.product_id, title: v.product_title, variant_title: v.title === 'Default' ? '' : v.title, price: v.price, quantity: qty[v.id] || 1, image: v.image });
    };

    return (
        <Modal open={open} onCancel={onClose} footer={null} title={t('Add products')} width={600} destroyOnHidden>
            <Input.Search allowClear placeholder={t('Search products')} value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 12 }} />
            <Spin spinning={loading}>
                <div style={{ maxHeight: 460, overflow: 'auto' }}>
                    {!rows.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
                        <List dataSource={rows} renderItem={(p) => (
                            <List.Item style={{ display: 'block', padding: '6px 4px' }}>
                                <div onClick={() => expand(p)} style={{ cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <Thumb src={p.image?.src} size={36} />
                                    <div style={{ flex: 1 }}>{p.title}</div>
                                    <Text type="secondary">{money(p.price_min, currency)}</Text>
                                </div>
                                {openId === p.id && (
                                    <div style={{ paddingLeft: 44, marginTop: 6 }}>
                                        {!variants[p.id] ? <Spin size="small" /> : variants[p.id].map((v) => (
                                            <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                                                <Text style={{ flex: 1 }}>{v.title === 'Default' ? p.title : v.title} <Text type="secondary" style={{ fontSize: 12 }}>{v.sku}</Text></Text>
                                                <Text>{money(v.price, currency)}</Text>
                                                <InputNumber size="small" min={1} value={qty[v.id] || 1} onChange={(x) => setQty((s) => ({ ...s, [v.id]: x || 1 }))} style={{ width: 64 }} />
                                                <Button size="small" icon={<PlusOutlined />} onClick={() => add(v)} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </List.Item>
                        )} />
                    )}
                </div>
            </Spin>
            <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 8 }}><Button type="primary" onClick={onClose}>{t('Done')}</Button></Space>
        </Modal>
    );
};

export default ShopVariantPicker;
